import React, { useCallback, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { Upload } from '../../../../assets';
import { useDropzone } from 'react-dropzone';
import { ConfirmationModal, Loader } from '../../../shared';
import { useDispatch, useSelector } from 'react-redux';
import { fileUploadActions } from '../../../../redux/reducers/filesUploadReducer';
import { getFiles,
    uploadFiles } from '../../../../redux/thunks/filesUploadThunk';

const UploadFile = () => {
    const dispatch = useDispatch();
    const { isUploadOpen, isUploading } = useSelector(
        (state) => state.filesUpload
    );
    const { company } = useSelector((state) => state.company);
    const [fileData, setFileData] = useState(null);
    const [fileError, setFileError] = useState(null);
    const { handleUploadModal } = fileUploadActions;
    const maxSize = 50 * 1024 * 1024; // 10 MB
    const onDrop = useCallback((acceptedFiles, fileRejections) => {
        if (fileRejections.length > 0) {
            // Collect all error messages
            const errorMessages = fileRejections
                .map((rejection) => {
                    const { file, errors } = rejection;
                    return errors
                        .map((error) => {
                            if (error.code === 'file-too-large') {
                                return `File ${
                                    file.name
                                } is too large. Maximum size is ${
                                    maxSize / (1024 * 1024)
                                } MB.`;
                            } else if (error.code === 'file-invalid-type') {
                                return `Invalid file type for ${file.name}. Please upload a supported(jpg,jpeg,png,doc,text,csv,excel,ppt) file type.`;
                            }
                            return `Error with ${file.name}: ${error.message}`;
                        })
                        .join(' ');
                })
                .join(' ');

            setFileError(errorMessages);
            setFileData(null);
        } else {
            const file = acceptedFiles[0];
            setFileData(file);
            setFileError(null);
        }
    }, []);

    const { getRootProps, getInputProps } = useDropzone({
        onDrop,
        accept: {
            'image/png': ['.png'],
            'image/jpeg': ['.jpg', '.jpeg'],
            'application/pdf': ['.pdf'],
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
                ['.docx'],
            'application/msword': ['.doc'],
            'text/plain': ['.txt'],
            'text/csv': ['.csv'],
            'application/vnd.ms-excel': ['.xls'],
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
                ['.xlsx'],
            'application/vnd.ms-powerpoint': ['.ppt'],
            'application/vnd.openxmlformats-officedocument.presentationml.presentation':
                ['.pptx'],
            'application/vnd.openxmlformats-officedocument.presentationml.slideshow':
                ['.ppsx']
        },
        maxSize
    });

    const handleUploadFile = () => {
        if (fileData) {
            const formData = new FormData();
            formData.append('file', fileData);
            formData.append('company_id', company.id);
            dispatch(uploadFiles({ data: formData })).then((result) => {
                if (result.payload.status_code === 200) {
                    dispatch(getFiles({ companyId: company.id }));
                    onClosed();
                }
            });
        } else {
            setFileError('Please choose a file to upload.');
        }
    };

    const onClosed = () => {
        setFileData(null);
        dispatch(handleUploadModal());
    };

    const renderUpload = () => (
        <Box className="flex-column-space-between gap-20 pt-24px">
            <Box className="pb-12px text-align-center" />
            <Box
                className="flex-column-center border-1-gray-300 br-16px padding-tb-16-rl-24 wh-100 icon"
                {...getRootProps()}
            >
                <input {...getInputProps()} />
                <Box className="wh-40px bg-gray-100 br-50 border-6-gray-50">
                    <Upload />
                </Box>
                <Box className="flex-row gap-4">
                    <Typography className="text-sm-medium primary-600">
                        Click to upload
                    </Typography>
                    <Typography className="text-sm-medium gray-500">
                        or drag and drop
                    </Typography>
                </Box>
                <Box>
                    <Typography className="text-xs-regular gray-500">
                        Files (max 50mb)
                    </Typography>
                </Box>
                <span className="text-lg-medium primary-600">
                    {fileData?.name}
                </span>
                <span className="text-xs-regular error-500">{fileError}</span>
            </Box>
            {isUploading && <Loader classNames="flex-row-center ml-40-percent" height="15%" width="15%" />}
        </Box>
    );

    return (
        <ConfirmationModal
            content={renderUpload()}
            customClass={true}
            open={isUploadOpen}
            primaryButtonText={'Upload'}
            title={
                <Typography className="queries-table-title">
                    Upload File
                </Typography>
            }
            onClose={onClosed}
            onConfirm={handleUploadFile}
        />
    );
};

export default UploadFile;
